import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Button, Stack, Typography } from '@mui/material'

import PageContainer from '@/components/ui/containers/PageContainer'
import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'

import { useDeleteContractMutation, useGetContractsQuery } from '@/core/services/contract'

import { getApiErrorMessage } from '@/utils/errors'

import ContractsTable from './components/ContractsTable'

export default function ContractsPage() {
  const { pathname } = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { data: contracts } = useGetContractsQuery()
  const [deleteContract, { isLoading: isDeletingContract }] = useDeleteContractMutation()

  const [deletingContractId, setDeletingContractId] = useState<string>()
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)

  const contractsData = useMemo(
    () =>
      contracts?.map((contract) => ({
        id: contract._id,
        canDelete: true,
        establishment: contract.establishment.name,
        licenses: contract.licenseUsages.length,
        name: contract.name,
      })) ?? [],
    [contracts]
  )

  return (
    <PageContainer>
      <Stack spacing={4}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h2">{t('page.contracts.title')}</Typography>
          <Stack direction="row">
            <Button component={Link} to={`${pathname}/create`} variant="contained">
              {t('common:button.create.label')}
            </Button>
          </Stack>
        </Stack>
        <ContractsTable
          data={contractsData}
          onDeleteClick={(licenseId) => {
            setDeletingContractId(licenseId)
            setOpenDeleteConfirmationDialog(true)
          }}
        />
      </Stack>
      <DeleteConfirmationDialog
        isDeleting={isDeletingContract}
        maxWidth={800}
        onClose={() => {
          setOpenDeleteConfirmationDialog(false)
        }}
        onDelete={async () => {
          if (deletingContractId) {
            try {
              await deleteContract(deletingContractId).unwrap()
            } catch (error) {
              enqueueSnackbar(getApiErrorMessage(error, t('notification.license.delete.error')), { variant: 'error' })
            }
            setDeletingContractId(undefined)
            setOpenDeleteConfirmationDialog(false)
          }
        }}
        open={openDeleteConfirmationDialog}
        title={t('dialog.deleteConfirmation.contract.title')}
        message={t('dialog.deleteConfirmation.contract.message')}
      />
    </PageContainer>
  )
}
